// lib/youtube.ts
// Pure helpers cho YouTube video — KHÔNG phụ thuộc API key, dùng được cả FE+SSR.

import type { Lesson, VideoContentData } from "@/types/lesson";

/**
 * Parse video ID từ mọi format URL YouTube phổ biến:
 * - https://www.youtube.com/watch?v=dQw4w9WgXcQ
 * - https://www.youtube.com/watch?v=dQw4w9WgXcQ&t=42s
 * - https://youtu.be/dQw4w9WgXcQ
 * - https://youtu.be/dQw4w9WgXcQ?si=xxx
 * - https://www.youtube.com/embed/dQw4w9WgXcQ
 * - https://www.youtube.com/shorts/dQw4w9WgXcQ
 * - https://m.youtube.com/watch?v=dQw4w9WgXcQ
 */
export function parseYoutubeId(input: string | null | undefined): string | null {
  if (!input?.trim()) return null;
  const url = input.trim();
  const re = /(?:youtube\.com\/(?:watch\?(?:[^&]*&)*v=|embed\/|shorts\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const m = url.match(re);
  return m?.[1] ?? null;
}

export function isYoutubeUrl(input: string | null | undefined): boolean {
  return parseYoutubeId(input) !== null;
}

/** Thumbnail public CDN — không cần API key. hqdefault luôn tồn tại (480x360). */
export function getYoutubeThumbnail(
  videoId: string,
  quality: "default" | "mqdefault" | "hqdefault" | "sddefault" | "maxresdefault" = "hqdefault"
): string {
  return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
}

/** Embed URL với privacy mode (youtube-nocookie) — chuẩn GDPR. */
export function getYoutubeEmbedUrl(videoId: string, autoplay = false): string {
  const params = new URLSearchParams();
  if (autoplay) params.set("autoplay", "1");
  params.set("rel", "0");            // không hiện video gợi ý kênh khác
  params.set("modestbranding", "1"); // giảm logo YouTube
  const qs = params.toString();
  return `https://www.youtube-nocookie.com/embed/${videoId}${qs ? `?${qs}` : ""}`;
}

/**
 * Extract YouTube video ID từ lesson nếu là video YouTube hợp lệ.
 * Ưu tiên content_data.external_id (đã parse server-side),
 * fallback parse content_url (defensive cho legacy data).
 */
export function getLessonYoutubeId(
  lesson: Pick<Lesson, "content_type" | "content_url" | "content_data">
): string | null {
  if (lesson.content_type !== "video") return null;
  const cd = lesson.content_data as VideoContentData | undefined;
  if (cd?.provider === "youtube" && cd.external_id) {
    return cd.external_id;
  }
  // Fallback: legacy lesson chưa có content_data → parse trực tiếp từ URL
  return parseYoutubeId(lesson.content_url);
}